import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Navigation, User, Zap, CheckCircle2, Users, Copy } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { WHATSAPP_PASSENGERS_GROUP } from "../../lib/troy";

const RIDE_GROUP_LINK = WHATSAPP_PASSENGERS_GROUP;

const INITIAL = { name: "", origin: "", destination: "" };

export default function RideRequest() {
  const [form, setForm] = useState(INITIAL);
  const [sent, setSent] = useState(false);
  const [countdown, setCountdown] = useState(8);
  const [rideMsg, setRideMsg] = useState("");

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  useEffect(() => {
    if (!sent) return;
    if (countdown <= 0) {
      window.location.href = RIDE_GROUP_LINK;
      return;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [sent, countdown]);

  const copyMsg = async (msg) => {
    try {
      await navigator.clipboard.writeText(msg);
      return true;
    } catch {
      return false;
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.origin.trim() || !form.destination.trim()) {
      toast.error("Preencha nome, onde retirar e para onde levar.");
      return;
    }
    const msg = [
      "🚗 *Solicitação de corrida — Troy*",
      "",
      `*Nome:* ${form.name.trim()}`,
      `*Onde retirar:* ${form.origin.trim()}`,
      `*Para onde levar:* ${form.destination.trim()}`,
    ].join("\n");

    // Abertura síncrona no clique (não é bloqueada por popup blocker)
    window.open(RIDE_GROUP_LINK, "_blank");

    const copied = await copyMsg(msg);
    setRideMsg(msg);
    setForm(INITIAL);
    setCountdown(8);
    setSent(true);
    if (copied) {
      toast.success("Mensagem copiada! Cole no grupo e envie.", { duration: 8000 });
    }
  };

  return (
    <section
      id="pedir-corrida"
      data-testid="ride-request-section"
      className="relative py-20 md:py-28 bg-[#121215] border-y border-white/5"
    >
      <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_50%_0%,rgba(255,204,0,0.1),transparent_55%)]" />

      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10">
          <p className="text-xs tracking-[0.25em] uppercase text-[#FFCC00] font-bold mb-3">
            Peça agora
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] mb-4 text-balance">
            Solicite sua <span className="text-[#FFCC00]">corrida</span> em segundos.
          </h2>
          <p className="text-white/70 text-lg max-w-xl mx-auto">
            Preencha e envie direto para o nosso WhatsApp. Simples assim.
          </p>
        </div>

        {sent ? (
          <div
            data-testid="ride-success-card"
            className="rounded-3xl bg-[#18181C] border border-[#FFCC00]/15 p-10 md:p-12 text-center"
          >
            <CheckCircle2 className="w-14 h-14 text-[#25D366] mx-auto mb-4" />
            <h3 className="font-display text-2xl md:text-3xl font-black mb-3">
              Sua mensagem está pronta!
            </h3>
            <p className="text-white/60 mb-5">
              Ela já foi <strong className="text-white">copiada</strong> — no grupo da Troy, é só{" "}
              <strong className="text-[#FFCC00]">colar e enviar</strong>.
            </p>
            <div
              data-testid="ride-message-preview"
              className="rounded-2xl bg-[#1C1C22] border border-[#FFCC00]/15 p-5 text-left text-sm text-white/80 whitespace-pre-wrap mb-4 max-w-md mx-auto"
            >
              {rideMsg}
            </div>
            <button
              type="button"
              data-testid="ride-copy-msg-btn"
              onClick={async () => {
                const ok = await copyMsg(rideMsg);
                ok ? toast.success("Mensagem copiada!") : toast.error("Não foi possível copiar. Selecione o texto acima.");
              }}
              className="inline-flex items-center gap-2 text-sm font-bold text-[#FFCC00] hover:text-[#E6B800] mb-7 transition-colors"
            >
              <Copy className="w-4 h-4" /> Copiar mensagem novamente
            </button>
            <p className="text-white/40 text-sm mb-5" data-testid="ride-group-countdown">
              Abrindo o grupo em {countdown}s...
            </p>
            <a
              href={RIDE_GROUP_LINK}
              data-testid="ride-group-btn"
              className="inline-flex items-center justify-center gap-3 w-full sm:w-auto bg-[#25D366] text-black font-bold py-4 px-10 rounded-xl hover:bg-[#1FB955] transition-colors text-lg shadow-[0_0_24px_rgba(37,211,102,0.35)]"
            >
              <Users className="w-5 h-5" />
              Colar e enviar no grupo
            </a>
            <button
              type="button"
              data-testid="ride-new-request-btn"
              onClick={() => setSent(false)}
              className="block mx-auto mt-6 text-sm text-white/50 hover:text-white underline underline-offset-4"
            >
              Pedir outra corrida
            </button>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            noValidate
            data-testid="ride-request-form"
            className="card-accent-top relative rounded-3xl bg-[#18181C] border border-[#FFCC00]/15 p-7 md:p-10"
          >
            <div className="absolute -inset-1 bg-[#FFCC00]/10 blur-3xl rounded-3xl -z-10" />
            <div className="space-y-5">
              <div>
                <Label htmlFor="ride-name" className="text-white/80 text-sm font-semibold mb-2 block">
                  Nome do Cliente *
                </Label>
                <div className="relative">
                  <User className="w-4 h-4 text-white/35 absolute left-4 top-1/2 -translate-y-1/2" />
                  <Input
                    id="ride-name"
                    name="name"
                    data-testid="ride-field-name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Seu nome"
                    className="bg-[#1C1C22] border-white/10 h-12 rounded-xl pl-11 text-white placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="ride-origin" className="text-white/80 text-sm font-semibold mb-2 block">
                    Onde retirar *
                  </Label>
                  <div className="relative">
                    <MapPin className="w-4 h-4 text-[#FFCC00] absolute left-4 top-1/2 -translate-y-1/2" />
                    <Input
                      id="ride-origin"
                      name="origin"
                      data-testid="ride-field-origin"
                      value={form.origin}
                      onChange={onChange}
                      placeholder="Ex: Rua A, 123 — General Carneiro"
                      className="bg-[#1C1C22] border-white/10 h-12 rounded-xl pl-11 text-white placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="ride-destination" className="text-white/80 text-sm font-semibold mb-2 block">
                    Para onde levar *
                  </Label>
                  <div className="relative">
                    <Navigation className="w-4 h-4 text-[#FF3B30] absolute left-4 top-1/2 -translate-y-1/2" />
                    <Input
                      id="ride-destination"
                      name="destination"
                      data-testid="ride-field-destination"
                      value={form.destination}
                      onChange={onChange}
                      placeholder="Ex: Centro de Sabará"
                      className="bg-[#1C1C22] border-white/10 h-12 rounded-xl pl-11 text-white placeholder:text-white/30 focus-visible:ring-[#FFCC00] focus-visible:border-[#FFCC00]"
                    />
                  </div>
                </div>
              </div>
            </div>

            <button
              type="submit"
              data-testid="ride-submit-btn"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-black text-lg py-5 px-8 rounded-xl hover:bg-[#E6B800] transition-all shadow-[0_0_32px_rgba(255,204,0,0.4)]"
            >
              <Zap className="w-5 h-5" />
              Solicitar Corrida agora
            </button>
            <p className="mt-3 text-xs text-white/50 text-center">
              Abre o WhatsApp com seu pedido pronto e depois te levamos para o grupo da Troy.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}
